import { DeleteConfirmationDialog } from '@components/dialogs/DeleteConfirmationDialog'
import { DataTable } from '@components/tables'
import {
  Container,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Paper,
} from '@mui/material'
import { GridColumns, GridRowId } from '@mui/x-data-grid-pro'
import { Actions } from '@state/actions'
import { useSelector } from '@state/store'
import { findAllRooms, removeDoctorOffice } from '@state/thunks/roomThunk'
import { findAll as findAllSites } from '@state/thunks/siteThunk'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { DialogTitle } from '@components/titles'
import { Office, RoomType, Site } from '@services/api'
import { removeSites } from '@state/reducers/siteReducer'
import { OfficesForm } from './OfficesForm'
import { EditIcon } from 'lucide-react'

export default function Offices() {
  const dispatch = useDispatch()
  const [openAddDialog, setOpenAddDialog] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [selectedRows, setSelectedRows] = useState<number[]>([])
  const [editingOffice, setEditingOffice] = useState<Office | undefined>(
    undefined,
  )

  const { loading, rooms } = useSelector(({ room, loading }) => ({
    rooms: room.rooms.filter((_room) => _room.type === RoomType.DoctorOffice),
    loading:
      loading[Actions.UPDATE_OFFICE] ||
      loading[Actions.CREATE_OFFICE] ||
      loading[Actions.ROOM_FIND_ALL],
  }))

  const columns2: GridColumns = [
    { field: 'label', headerName: 'Label', flex: 1 },
    { field: 'code', headerName: 'Code', flex: 1 },
    {
      field: 'site',
      headerName: 'Site',
      flex: 1,
      valueFormatter: (params) => {
        return (params.value as unknown as Site)?.label
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      align: 'right',
      renderCell: (params) => {
        return (
          <IconButton
            size="small"
            onClick={() => {
              setEditingOffice(params.row)
              setOpenAddDialog(true)
            }}
          >
            <EditIcon />
          </IconButton>
        )
      },
    },
  ]

  const onRefresh = useCallback(() => {
    dispatch(findAllRooms())
    dispatch(findAllSites())
  }, [dispatch])

  useEffect(() => {
    onRefresh()
    return () => {
      dispatch(removeSites())
    }
  }, [dispatch, onRefresh])

  const _onSelectionChange = (selection: GridRowId[]) => {
    setSelectedRows(selection as number[])
  }
  const _onDeleteConfirm = () => {
    dispatch(removeDoctorOffice({ id: selectedRows }))
    setIsDeleteDialogOpen(false)
  }

  return (
    <Container maxWidth={false} style={{ height: '93%', padding: '0px 24px' }}>
      <Paper
        elevation={2}
        style={{ width: '100%', borderRadius: '0px 15px 15px 15px' }}
      >
        <DataTable
          rows={rooms}
          columns={columns2}
          loading={loading}
          toolBarProps={{
            title: 'rooms',
            icon: 'fab fa-uikit',
            onAdd: () => setOpenAddDialog(true),
            onRefresh,
            onDelete: () => {
              selectedRows.length
                ? setIsDeleteDialogOpen(true)
                : alert('please select at least one office')
            },
          }}
          detailPanelHeight={400}
          checkboxSelection
          onSelectionModelChange={_onSelectionChange}
        />
      </Paper>
      <Dialog
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        maxWidth="lg"
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle
          title="addOffice"
          onClose={() => setOpenAddDialog(false)}
          format
        />
        <DialogContent>
          <OfficesForm
            onClose={() => setOpenAddDialog(false)}
            row={editingOffice}
          />
        </DialogContent>
      </Dialog>

      <DeleteConfirmationDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={_onDeleteConfirm}
        title="Confirmer la suppression"
        description="Etes-vous sûr de vouloir supprimer les bureaux sélectionnés ?"
      />
    </Container>
  )
}
